import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ROUTE_PATHS } from '../routes/routePaths';
import { closeAnnouncementPopup, useGetAnnouncementPopups } from '../services/announcementService';
import { useEncryptMutation } from '../services/reportServices';
import type { AnnouncementPopup } from '../types/announcement';
import { logger } from '../util/logger';

const ANNOUNCEMENT_POP_SHOW_KEY = 'announcementPopShow';

type PopupPosition = 'top' | 'bottom' | 'corner';

interface PopupBarProps {
  popup: AnnouncementPopup;
  position: PopupPosition;
  onClose: (popup: AnnouncementPopup, doNotRemind: boolean) => void;
}

const ANNOUNCEMENT_POPUP_TYPE_ID = {
  HEADER: 101,
  FOOTER: 102,
  CORNER: 103,
} as const;

const POPUP_STYLES = `
.myAlert-bottom {
  position: fixed;
  bottom: 0;
  left: 2%;
  width: 96%;
  z-index: 999;
  font-size: 0.9rem !important;
}
.myAlert-bottom p {
  margin-bottom: 0 !important;
}

.myAlert-cornerAlert {
  position: fixed;
  bottom: 0;
  left: 2%;
  width: 40%;
  z-index: 999;
  font-size: 0.9rem !important;
}
.myAlert-cornerAlert p {
  margin-bottom: 0 !important;
}

.myAlert-top {
  position: fixed;
  top: 80px;
  left: 2%;
  width: 96%;
  z-index: 999;
  font-size: 0.9rem !important;
}
.myAlert-top p {
  margin-bottom: 0 !important;
}

button.close {
  padding: 0;
  background-color: transparent;
  border: 0;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}
.close {
  float: right;
  font-size: 1rem;
  font-weight: normal;
  line-height: 1;
  color: #000;
  text-shadow: 0 1px 0 #fff;
  background: none;
  cursor: pointer;
  border: 0;
}

button.closeIcon {
  box-sizing: content-box;
  width: 1em;
  height: 1em;
  color: #000;
  background: transparent url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23000'%3e%3cpath d='M.293.293a1 1 0 011.414 0L8 6.586 14.293.293a1 1 0 111.414 1.414L9.414 8l6.293 6.293a1 1 0 01-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 01-1.414-1.414L6.586 8 .293 1.707a1 1 0 010-1.414z'/%3e%3c/svg%3e") center/0.8em auto no-repeat;
  border: 0;
  border-radius: 0.25rem;
  opacity: 0.5;
  position: absolute;
  right: 0;
  padding-right: 0.8rem;
}
.closeTop {
  top: 0 !important;
  padding-top: 1rem;
}
.closeBottom {
  bottom: 5px !important;
  padding-bottom: 0.5rem;
}

.closeText {
  font-size: 1rem;
  font-weight: normal;
  line-height: 1;
  color: #000;
  text-shadow: 0 1px 0 #fff;
  position: absolute;
  right: 0;
  padding-right: 1rem;
  padding-top: 0.3rem;
  background: none;
  border: 0;
  cursor: pointer;
}
`;

const wrapperClassByPosition: Record<PopupPosition, string> = {
  top: 'myAlert-top',
  bottom: 'justify-content-center align-items-center myAlert-bottom',
  corner: 'justify-content-center align-items-center myAlert-cornerAlert',
};

const PopupBar = ({ popup, position, onClose }: PopupBarProps) => {
  const [doNotRemind, setDoNotRemind] = useState(false);
  const showDoNotRemindCheckbox = popup.closeButtonEffect === 'donotremindwithcheckbox';
  const closeButtonNumber = Number(popup.closeButton);

  const paddingRight = useMemo(() => {
    if (showDoNotRemindCheckbox) {
      return;
    }

    if (position === 'bottom') {
      return closeButtonNumber === 1 ? '65px' : '3rem';
    }

    if (position === 'top') {
      return closeButtonNumber === 2 ? '2.5rem' : undefined;
    }

    if (position === 'corner') {
      return closeButtonNumber === 2 ? '1.5rem' : undefined;
    }

    return;
  }, [closeButtonNumber, position, showDoNotRemindCheckbox]);

  const handleClose = (withDoNotRemind: boolean) => {
    void onClose(popup, withDoNotRemind);
  };

  return (
    <div
      className={popup.popupStyleClass ?? 'alert alert-warning py-2 mb-0'}
      style={{
        position: 'relative',
        ...(paddingRight ? { paddingRight } : {}),
      }}
    >
      {position === 'top' && (
        <>
          <Link
            to="#"
            className="close"
            aria-label="close"
            onClick={(event) => {
              event.preventDefault();
              handleClose(false);
            }}
          >
            {popup.closeButtonText}
          </Link>

          <div dangerouslySetInnerHTML={{ __html: popup.description }} />
        </>
      )}

      {position !== 'top' && (
        <>
      {popup.closeButtonEffect !== 'donotremindwithcheckbox' && (
        <>
          {closeButtonNumber === 2 ? (
            <button
              type="button"
              className="closeIcon closeTop"
              onClick={() => {
                handleClose(false);
              }}
              aria-label="Close"
            />
          ) : (
            <Link
              to="#"
              className="closeText linkRed"
              aria-label="close"
              onClick={(event) => {
                event.preventDefault();
                handleClose(false);
              }}
            >
              {popup.closeButtonText}
            </Link>
          )}
        </>
      )}

      {/* Description comes from backend and may contain HTML */}
      {/* eslint-disable-next-line react/no-danger */}
      <div dangerouslySetInnerHTML={{ __html: popup.description }} />

      {showDoNotRemindCheckbox && (
        <>
          <hr className="mt-2 mb-2" />
          <div className="text-end me-4 form-check-label align-middle">
            <label className="form-check-label me-3">
              <input
                type="checkbox"
                className="form-check-input align-middle me-1 mt-0"
                checked={doNotRemind}
                onChange={(event) => {
                  setDoNotRemind(event.target.checked);
                }}
              />
              {' '}
              Do not remind me about this again
            </label>
            <button
              type="button"
              className="closeIcon closeBottom"
              onClick={() => {
                handleClose(doNotRemind);
              }}
              aria-label="Close"
            />
          </div>
        </>
      )}
        </>
      )}
    </div>
  );
};

const AnnouncementPopups = () => {
  const location = useLocation();

  const isTermsPage = location.pathname === ROUTE_PATHS.TERMS_AND_CONDITIONS;
  const isCKEditorPage =
    location.pathname.startsWith('/ckeditor') ||
    location.pathname === ROUTE_PATHS.RICH_TEXT_BOX_POPUP;

  const shouldShowFromSession =
    typeof window !== 'undefined' &&
    window.sessionStorage.getItem(ANNOUNCEMENT_POP_SHOW_KEY) === 'true';

  const enabled = shouldShowFromSession && !isTermsPage && !isCKEditorPage;

  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useGetAnnouncementPopups(['HEADER_ALERT_TYPE', 'FOOTER_ALERT_TYPE','CORNER_ALERT_TYPE'], enabled);

  const encryptMutation = useEncryptMutation();

  const [dismissedIds, setDismissedIds] = useState<Array<number>>([]);

  useEffect(() => {
    if (enabled) {
      void refetch();
    }
  }, [enabled, location.pathname, refetch]);

  const popups = useMemo(() => {
    const items = data?.data ?? [];
    return items.filter((popup) => !dismissedIds.includes(popup.announcementId));
  }, [data, dismissedIds]);

  const handleClose = async (popup: AnnouncementPopup, doNotRemind: boolean) => {
    // Match CF: donotShowAgainVal = -1 for simple close (no checkbox), 0 or 1 when "don't remind" checkbox is used
    const donotShowAgainValue =
      popup.closeButtonEffect === 'donotremindwithcheckbox' ? (doNotRemind ? 1 : 0) : -1;

    try {
      const encryptResponse = await encryptMutation.mutateAsync(String(popup.announcementId));
      const eventAnnouncementId = encryptResponse?.data ?? '';
      void closeAnnouncementPopup(eventAnnouncementId, donotShowAgainValue);
      setDismissedIds((previous) => [...previous, popup.announcementId]);
      void refetch();
    } catch (error) {
      logger.error('Failed to close announcement popup', error);
    }
  };

  if (!enabled || isLoading || isError || popups.length === 0) {
    return null;
  }

  const headerPopups = popups.filter(
    (popup) => popup.announcementPopupTypeId === ANNOUNCEMENT_POPUP_TYPE_ID.HEADER,
  );
  const footerPopups = popups.filter(
    (popup) => popup.announcementPopupTypeId === ANNOUNCEMENT_POPUP_TYPE_ID.FOOTER,
  );
  const cornerPopups = popups.filter(
    (popup) => popup.announcementPopupTypeId === ANNOUNCEMENT_POPUP_TYPE_ID.CORNER,
  );

  return (
    <>
      <style>{POPUP_STYLES}</style>
      {headerPopups.length > 0 && (
        <div className={wrapperClassByPosition.top}>
          {headerPopups.map((popup) => (
            <PopupBar
              key={popup.announcementId}
              popup={popup}
              position="top"
              onClose={handleClose}
            />
          ))}
        </div>
      )}
      {cornerPopups.length > 0 && (
        <div className={wrapperClassByPosition.corner}>
          {cornerPopups.map((popup) => (
            <PopupBar
              key={popup.announcementId}
              popup={popup}
              position="corner"
              onClose={handleClose}
            />
          ))}
        </div>
      )}
      {footerPopups.length > 0 && (
        <div className={wrapperClassByPosition.bottom}>
          {footerPopups.map((popup) => (
            <PopupBar
              key={popup.announcementId}
              popup={popup}
              position="bottom"
              onClose={handleClose}
            />
          ))}
        </div>
      )}
    </>
  );
};

export default AnnouncementPopups;

